import { V2OrderSideType } from '@flipster/utils/types/base';
import { valueof } from '@flipster/utils/typescript';

import { ReqDatetime, ReqPagination, ResPagination } from '../../types';
import { OrderBase } from '../order/types';

export interface HistoryBaseParams extends ReqPagination, ReqDatetime {
  symbol?: string;
}

export interface GetPnlHistoriesParams extends HistoryBaseParams {
  interval: string;
  historyType: string;
}

export interface FuturesPnlHistory {
  accountValue: string;
  cumPnl: string;
  cumRealizedPnl: string;
  pnl: string;
  realizedPnl: string;
  timestamp: string;
  unrealizedPnl: string;
}

export interface SpotPnlHistory {
  spotCumPnl: string;
  spotPnl: string;
  timestamp: string;
}

export interface GetPnlHistoriesResponse extends ResPagination {
  history: Array<FuturesPnlHistory | SpotPnlHistory>;
}

export interface FundingHistory {
  accountId: string;
  cumFee: string;
  execFee: string;
  execId: string;
  execType: string;
  feeRate: string;
  price: string;
  settlementCurrency: string;
  side: string;
  symbol: string;
  transactTime: number;
}

export interface GetFundingHistoriesResponse extends ResPagination {
  history: FundingHistory[];
}

export interface GetFillHistoriesParams extends HistoryBaseParams {
  orderId?: string;
  execType?: string;
  productType?: ProductType;
}

export const ExecTypeEnum = {
  New: 'EXEC_TYPE_NEW',
  Invalid: 'EXEC_TYPE_INVALID',
  Trade: 'EXEC_TYPE_TRADE',
  Canceled: 'EXEC_TYPE_CANCELED',
  Replaced: 'EXEC_TYPE_REPLACED',
  Rejected: 'EXEC_TYPE_REJECTED',
} as const;

export type ExecTypeType = valueof<typeof ExecTypeEnum>;

export interface FillHistory extends OrderBase {
  aggressorIndicator: boolean;
  execFee: string;
  execId: string;
  execType: ExecTypeType;
  feeRate: string;
  lastPrice: string;
  lastQty: string;
  lastTrade: boolean;
  matchId: string;
  matchTime: number;
  settlementCurrency: string;
}

export interface GetFillHistoriesResponse extends ResPagination {
  history: FillHistory[];
}

export enum ProductType {
  Perpetual = 'PRODUCT_TYPE_PERPETUAL_SWAP',
  Spot = 'PRODUCT_TYPE_SPOT',
}

export interface GetOrderHistoriesParams extends HistoryBaseParams {
  hideCanceledOrder?: boolean;
  productType?: ProductType;
}

export type OrderLinkType = { type: string; orderId: string };

export const TriggerPriceDirectionEnum = {
  Invalid: 'TRIGGER_PRICE_DIRECTION_INVALID',
  Up: 'TRIGGER_PRICE_DIRECTION_UP',
  Down: 'TRIGGER_PRICE_DIRECTION_DOWN',
} as const;

export type TriggerPriceDirectionType = valueof<typeof TriggerPriceDirectionEnum>;

export const TriggerPriceTypeEnum = {
  Invalid: 'TRIGGER_PRICE_TYPE_INVALID',
  MarkPrice: 'TRIGGER_PRICE_TYPE_MARK',
  LastPrice: 'TRIGGER_PRICE_TYPE_LAST_TRADE',
} as const;

export type TriggerPriceTypeType = valueof<typeof TriggerPriceTypeEnum>;

export interface OrderHistory extends OrderBase {
  cumFee: string;
  initLeverage: number | null; // spot의 경우 initLeverage가 null
  link: OrderLinkType[];
  orderAmount: string;
  triggerPrice: string;
  triggerPriceDirection: TriggerPriceDirectionType;
  triggerPriceType: TriggerPriceTypeType;
  triggerSymbol: string;
  triggered: boolean;
}

export interface GetOrderHistoriesResponse extends ResPagination {
  history: OrderHistory[];
}

export interface GetWalletHistoriesParams extends HistoryBaseParams {
  transferType?: string;
}

export const MediumTypeEnum = {
  Invalid: 'MEDIUM_TYPE_INVALID',
  Blockchain: 'MEDIUM_TYPE_BLOCKCHAIN',
  BlockchainFee: 'MEDIUM_TYPE_BLOCKCHAIN_FEE',
  Internal: 'MEDIUM_TYPE_INTERNAL',
} as const;

export type MediumTypeType = valueof<typeof MediumTypeEnum>;

export const TransferStateEnum = {
  // common
  Invalid: 'USER_TRANSFER_STATE_INVALID',
  Done: 'USER_TRANSFER_STATE_DONE',
  Canceled: 'USER_TRANSFER_STATE_CANCELED',
  Rejected: 'USER_TRANSFER_STATE_REJECTED',
  InvalidHash: 'USER_TRANSFER_STATE_INVALID_HASH',
  BouncedBack: 'USER_TRANSFER_STATE_BOUNCED_BACK',
  SystemFeeReady: 'USER_TRANSFER_STATE_SYSTEM_FEE_READY',

  // aml common
  PendingAmlCheck: 'USER_TRANSFER_STATE_PENDING_AML_CHECK',
  PendingAmlAlerts: 'USER_TRANSFER_STATE_PENDING_AML_ALERTS',
  PendingAmlApproval: 'USER_TRANSFER_STATE_PENDING_AML_APPROVAL',
  AmlRejected: 'USER_TRANSFER_STATE_AML_REJECTED',

  // withdrawal common
  BurnLoss: 'USER_TRANSFER_STATE_WITHDRAWAL_BURN_LOSS',
  PendingCoreRequest: 'USER_TRANSFER_STATE_WITHDRAWAL_PENDING_CORE_REQUEST',
  PendingUserConfirm: 'USER_TRANSFER_STATE_WITHDRAWAL_PENDING_USER_CONFIRM',
  PendingAdminApproval: 'USER_TRANSFER_STATE_WITHDRAWAL_PENDING_ADMIN_APPROVAL',
  PendingBroadcasting: 'USER_TRANSFER_STATE_WITHDRAWAL_PENDING_BROADCASTING',
  AdminApproved: 'USER_TRANSFER_STATE_WITHDRAWAL_ADMIN_APPROVED',
  AdminRejected: 'USER_TRANSFER_STATE_WITHDRAWAL_ADMIN_REJECTED',
  UserCancelRequested: 'USER_TRANSFER_STATE_WITHDRAWAL_USER_CANCEL_REQUESTED',
  TimeoutCancelRequested: 'USER_TRANSFER_STATE_WITHDRAWAL_TIMEOUT_CANCEL_REQUESTED',
  BroadcastedToNetwork: 'USER_TRANSFER_STATE_WITHDRAWAL_BROADCASTED_TO_NETWORK',
  CreatedAndFailedInNetwork: 'USER_TRANSFER_STATE_WITHDRAWAL_CREATED_AND_FAILED_IN_NETWORK',
  FailedBroadcasting: 'USER_TRANSFER_STATE_WITHDRAWAL_FAILED_BROADCASTING',
  FundLockReleased: 'USER_TRANSFER_STATE_WITHDRAWAL_FUND_LOCK_RELEASED',

  // withdrawal transaction monitoring
  TransactionMonitoringPendingReview: 'USER_TRANSFER_STATE_TRANSACTION_MONITORING_PENDING_REVIEW',
  TransactionMonitoringPendingApproval: 'USER_TRANSFER_STATE_TRANSACTION_MONITORING_PENDING_APPROVAL',

  // withdrawal travel rule
  TrReady: 'USER_TRANSFER_STATE_WITHDRAWAL_TR_READY',
  TrRejected: 'USER_TRANSFER_STATE_WITHDRAWAL_TR_REJECTED',
  TrPendingAuthorization: 'USER_TRANSFER_STATE_WITHDRAWAL_TR_PENDING_AUTHORIZATION',
  TrPendingVerifyAddress: 'USER_TRANSFER_STATE_WITHDRAWAL_TR_PENDING_VERIFY_ADDRESS',
  TrPendingVerifyVaspStart: 'USER_TRANSFER_STATE_WITHDRAWAL_TR_PENDING_VERIFY_VASP_START',
  PendingVerifyAddressRequest: 'USER_TRANSFER_STATE_WITHDRAWAL_TR_PENDING_VERIFY_ADDRESS_V2_REQUEST',
  PendingVerifyAddressResult: 'USER_TRANSFER_STATE_WITHDRAWAL_TR_PENDING_VERIFY_ADDRESS_V2_RESULT',
  TrPendingAdminApproval: 'USER_TRANSFER_STATE_WITHDRAWAL_TR_PENDING_ADMIN_APPROVAL',
  TrPendingVerifyVaspVerification: 'USER_TRANSFER_STATE_WITHDRAWAL_TR_PENDING_VERIFY_VASP_VERIFICATION',

  // deposit common
  PendingNetwork: 'USER_TRANSFER_STATE_DEPOSIT_PENDING_NETWORK',
  PendingTransfer: 'USER_TRANSFER_STATE_DEPOSIT_PENDING_TRANSFER',
  ConfirmedInNetwork: 'USER_TRANSFER_STATE_DEPOSIT_CONFIRMED_IN_NETWORK',

  // deposit aml
  AmlRejectedBalanceHold: 'USER_TRANSFER_STATE_AML_REJECTED_BALANCE_HOLD',

  // deposit travel rule
  DepositTrPendingCheck: 'USER_TRANSFER_STATE_DEPOSIT_TR_PENDING_CHECK',
  DepositTrPendingAdminApproval: 'USER_TRANSFER_STATE_DEPOSIT_TR_PENDING_ADMIN_APPROVAL',
} as const;

export type TransferStateType = valueof<typeof TransferStateEnum>;

export const TransferTypeEnum = {
  Invalid: 'USER_TRANSFER_TYPE_INVALID',
  Deposit: 'USER_TRANSFER_TYPE_DEPOSIT',
  Withdrawal: 'USER_TRANSFER_TYPE_WITHDRAWAL',
} as const;

export type TransferTypeType = valueof<typeof TransferTypeEnum>;

export interface WalletHistory {
  adminNote?: string | null;
  amlPublicReason?: string | null;
  blockchainAsset: string;
  blockchainName: string;
  completedAt?: string | null;
  createdAt: string;
  currency: string;
  destAddress: string;
  fee: string;
  id: string;
  mediumType: MediumTypeType;
  netAmount: string;
  totalAmount: string;
  transferState: TransferStateType;
  transferType: TransferTypeType;
  confirmations?: number | null;
  minConfirmations?: number | null;
  txid?: string | null;
}

export interface GetWalletHistoriesResponse extends ResPagination {
  history: WalletHistory[];
}

export interface GetConversionHistoriesParams extends HistoryBaseParams {
  fromCurrency?: string;
  toCurrency?: string;
}

export const ConversionStatusEnum = {
  Invalid: 'CURRENCY_CONVERSION_STATUS_INVALID',
  Completed: 'CURRENCY_CONVERSION_STATUS_COMPLETED',
  Pending: 'CURRENCY_CONVERSION_STATUS_PENDING',
  Expired: 'CURRENCY_CONVERSION_STATUS_EXPIRED',
  FailedDueToBalance: 'CURRENCY_CONVERSION_STATUS_FAILED_DUE_TO_BALANCE',
  FailedDueToRate: 'CURRENCY_CONVERSION_STATUS_FAILED_DUE_TO_RATE',
} as const;

export type ConversionStatusType = valueof<typeof ConversionStatusEnum>;

export interface ConversionHistory {
  confirmationTs: number;
  conversionStatus: ConversionStatusType;
  currencyConversionId: string;
  exchangeRate: string;
  expirationTs: number;
  fromAmount: string;
  fromCurrency: string;
  groupConversionId: string;
  groupConversionSeq: string;
  initiationTs: number;
  timestamp: number;
  toAmount: string;
  toCurrency: string;
}

export interface GetConversionHistoriesResponse extends ResPagination {
  history: ConversionHistory[];
}

export interface GetValidateAddressParams {
  blockchainName: string; // bitcoin
  address: string; // 2NBXsLTgzJqadN5Rw9G74giZY39arRKdEHj
  destinationTag?: string; // 12345
  memo?: string;
}

export interface GetValidateAddressResponse {
  address: string;
  addressParams: {
    destinationTag?: string;
    memo?: string;
  };
}

export interface ProfitBoostHistory {
  id: string;
  startTs: string;
  endTs: string;
  symbol: string;
  side: V2OrderSideType;
  profit: string;
  profitPct: string;
  cost: string;
  qty: string;
  entryPrice: string;
  closePrice: string;
}

export interface GetProfitBoostHistoriesResponse extends ResPagination {
  history: ProfitBoostHistory[];
}
